import { useState } from "react";
import { Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ICobrancaDetailRetorno,
  ISeguroNegociacaoRetorno,
} from "@/models/consignado/proposal-billings.model";
import {
  atualizarSeguroNegociacao,
  buscarSeguroNegociacoes,
  criarSeguroNegociacao,
  deletarSeguroNegociacao,
  gerarComprovanteDesligamento,
} from "@/services/consignado/proposta-cobranca.service";
import CollapsibleSection from "./CollapsibleSection";
import SinistroVinculo from "./SinistroVinculo";
import { SinistroFormValues } from "./SinistroFormModal";
import { useApiErrorToast } from "@/hooks/useApiErrorToast";
import { COBRANCA_DETALHE_QUERY_KEY, useCobrancaDetalhe } from "@/hooks/useCobrancaDetalhe";

type Props = {
  // UUID da cobrança (`cobranca_id` da listagem).
  cobrancaId: string | null;
};

export const SINISTROS_QUERY_KEY = "seguro-negociacoes";

// Seção "Sinistro" do detalhe da CCB — REAL (HIS-030). Motivo e URL do
// comprovante vêm do detalhe (useCobrancaDetalhe). Nasce colapsada e só
// dispara as queries na 1ª abertura.
//  - histórico de sinistro: GET/POST /cobrancas/:id/seguros/negociacoes,
//    PUT/DELETE /cobrancas/seguros/negociacoes/:id (TAR-060). A lista vem
//    ordenada por data_contato DESC, então depois de salvar/excluir
//    invalidamos em vez de mexer no cache.
//  - comprovante de desligamento: POST /cobrancas/:id/comprovante-desligamento
//    é síncrono desde a TAR-060 — devolve a URL na resposta, e o backend grava
//    o motivo do desligamento na cobrança durante a geração.
export default function SinistroSection({ cobrancaId }: Props) {
  const toast = useToast();
  const toastErro = useApiErrorToast();
  const queryClient = useQueryClient();
  const [aberta, setAberta] = useState(false);

  const { data: cobranca, isLoading: isLoadingDetalhe, isError } = useCobrancaDetalhe(cobrancaId, aberta);

  const sinistrosQueryKey = [SINISTROS_QUERY_KEY, cobrancaId];
  const { data: sinistros = [] } = useQuery<ISeguroNegociacaoRetorno[]>({
    queryKey: sinistrosQueryKey,
    queryFn: () => buscarSeguroNegociacoes(cobrancaId!),
    enabled: aberta && !!cobrancaId,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const invalidarSinistros = () => queryClient.invalidateQueries({ queryKey: sinistrosQueryKey });

  const gerarComprovante = useMutation({
    mutationFn: () => gerarComprovanteDesligamento(cobrancaId!),
    onSuccess({ url }) {
      queryClient.setQueryData<ICobrancaDetailRetorno>(
        [COBRANCA_DETALHE_QUERY_KEY, cobrancaId],
        (old) => (old ? { ...old, comprovante_desligamento_url: url } : old)
      );
      queryClient.invalidateQueries({ queryKey: [COBRANCA_DETALHE_QUERY_KEY, cobrancaId] });
      toast({
        title: "Comprovante de desligamento gerado",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: toastErro("Erro", "Erro ao gerar comprovante."),
  });

  const salvar = useMutation({
    mutationFn: ({ values, sinistro }: { values: SinistroFormValues; sinistro: ISeguroNegociacaoRetorno | null }) => {
      // datetime-local é hora local → ISO com fuso, pro backend gravar certo.
      const campos = {
        data_contato: new Date(values.data_contato).toISOString(),
        status: values.status,
        descricao: values.descricao.trim(),
      };

      return sinistro ? atualizarSeguroNegociacao(sinistro.id, campos) : criarSeguroNegociacao(cobrancaId!, campos);
    },
    onSuccess(_, { sinistro }) {
      toast({
        title: sinistro ? "Sinistro atualizado" : "Sinistro adicionado",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      invalidarSinistros();
    },
    onError: toastErro("Erro ao salvar sinistro"),
  });

  const excluir = useMutation({
    mutationFn: (sinistro: ISeguroNegociacaoRetorno) => deletarSeguroNegociacao(sinistro.id),
    onSuccess: invalidarSinistros,
    onError: toastErro("Erro ao excluir sinistro"),
  });

  const conteudo = isLoadingDetalhe ? (
    <Flex justifyContent="center" py={4}>
      <Spinner color="primary" />
    </Flex>
  ) : isError || !cobrancaId ? (
    <Text fontSize="sm" color="gray.500">
      Não foi possível carregar os dados de sinistro desta CCB.
    </Text>
  ) : (
    <SinistroVinculo
      motivo={cobranca?.motivo_desligamento}
      comprovanteUrl={cobranca?.comprovante_desligamento_url || null}
      onGerarComprovante={() => gerarComprovante.mutate()}
      isGeneratingComprovante={gerarComprovante.isPending}
      sinistros={sinistros}
      onSalvar={(values, sinistro) => salvar.mutateAsync({ values, sinistro })}
      isSaving={salvar.isPending}
      onExcluir={(sinistro) => excluir.mutate(sinistro)}
      excluindoId={excluir.isPending ? excluir.variables?.id ?? null : null}
    />
  );

  return (
    <CollapsibleSection
      title="Sinistro"
      defaultExpanded={false}
      onToggle={(expanded) => expanded && setAberta(true)}
    >
      {aberta && conteudo}
    </CollapsibleSection>
  );
}
